import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { lightTheme, darkTheme } from "../themes/antd-theme";
import { isNil } from "@kalodata/tools";

// 生效主题（实际渲染用）
export type Theme = "light" | "dark";

// 主题偏好（持久化用）。"system" 表示跟随系统/浏览器偏好
export type ThemePreference = "system" | "light" | "dark";

const STORAGE_KEY_PREFERENCE = "themePreference";
const LEGACY_STORAGE_KEY_THEME = "theme";

// 一次性清理旧 key：忽略历史 localStorage.theme，避免老用户被旧值锁定
if (typeof window !== "undefined") {
  try {
    if (window.localStorage.getItem(LEGACY_STORAGE_KEY_THEME) !== null) {
      window.localStorage.removeItem(LEGACY_STORAGE_KEY_THEME);
    }
  } catch {
    // ignore — localStorage 不可用时不影响主题逻辑
  }
}

const isThemePreference = (v: unknown): v is ThemePreference => v === "system" || v === "light" || v === "dark";

interface ThemePreferenceStorage {
  getItem: (key: string, initialValue: ThemePreference) => ThemePreference;
  setItem: (key: string, value: ThemePreference) => void;
  removeItem: (key: string) => void;
  subscribe: (key: string, callback: (value: ThemePreference) => void, initialValue: ThemePreference) => () => void;
}

// 自定义 storage：容错解析、非法值回退、跨标签订阅
const themePreferenceStorage: ThemePreferenceStorage = {
  getItem: (key, initialValue) => {
    if (typeof window === "undefined") return initialValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return initialValue;
      const parsed = JSON.parse(raw);
      return isThemePreference(parsed) ? parsed : initialValue;
    } catch {
      return initialValue;
    }
  },
  setItem: (key, value) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // ignore
    }
  },
  removeItem: (key) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
  subscribe: (key, callback, initialValue) => {
    if (typeof window === "undefined") return () => {};
    const listener = (e: StorageEvent) => {
      if (e.key !== key) return;
      try {
        if (isNil(e.newValue)) {
          callback(initialValue);
          return;
        }
        const parsed = JSON.parse(e.newValue);
        callback(isThemePreference(parsed) ? parsed : initialValue);
      } catch {
        callback(initialValue);
      }
    };
    window.addEventListener("storage", listener);
    return () => window.removeEventListener("storage", listener);
  },
};

// 主题偏好（持久化），默认 "system"
export const themePreferenceAtom = atomWithStorage<ThemePreference>(
  STORAGE_KEY_PREFERENCE,
  "system",
  themePreferenceStorage,
  { getOnInit: true },
);

// 系统当前是否偏好深色（响应式）
const getSystemPrefersDark = (): boolean => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
};

const systemPrefersDarkBaseAtom = atom<boolean>(getSystemPrefersDark());

systemPrefersDarkBaseAtom.onMount = (set) => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
  let media: MediaQueryList;
  try {
    media = window.matchMedia("(prefers-color-scheme: dark)");
  } catch {
    return;
  }
  set(media.matches);
  const listener = (e: MediaQueryListEvent) => set(e.matches);
  if (typeof media.addEventListener === "function") {
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }
  // 兼容老 Safari
  if (typeof (media as any).addListener === "function") {
    (media as any).addListener(listener);
    return () => (media as any).removeListener(listener);
  }
  return;
};

export const systemPrefersDarkAtom = atom((get) => get(systemPrefersDarkBaseAtom));

// 实际生效主题（resolved）— 派生自偏好与系统设置
// 注：此 atom 名称沿用 themeAtom，兼容老组件 useAtomValue(themeAtom) === "dark" 的写法
export const themeAtom = atom<Theme>((get) => {
  const pref = get(themePreferenceAtom);
  if (pref === "light" || pref === "dark") return pref;
  return get(systemPrefersDarkBaseAtom) ? "dark" : "light";
});

// Antd 主题配置 — 跟随生效主题
export const antdThemeConfig = {
  light: lightTheme,
  dark: darkTheme,
};

export const antdThemeAtom = atom((get) => {
  const currentTheme = get(themeAtom);
  return antdThemeConfig[currentTheme] || lightTheme;
});

export const isBodyBackgroundNearWhite = (threshold: number = 235): boolean => {
  try {
    const body = document.body;
    if (!body) return false;
    const bg = getComputedStyle(body).backgroundColor || "";
    const m = bg.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (!m) return false;
    const r = parseInt(m[1], 10);
    const g = parseInt(m[2], 10);
    const b = parseInt(m[3], 10);
    return r >= threshold && g >= threshold && b >= threshold;
  } catch {
    return false;
  }
};

// 把生效主题应用到 DOM（添加/移除 .dark 类）
const applyDarkClass = (enable: boolean) => {
  const root = document.documentElement;
  if (enable) {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  void root.offsetHeight; // 强制重排，确保样式立即生效
};

// 主题切换 atom（写入：偏好；读取：保持向后兼容，返回生效主题）
// 兼容历史调用：setTheme("light" | "dark")；同时支持新值 "system"
export const themeActionAtom = atom(
  (get) => get(themeAtom),
  (_get, set, next: ThemePreference) => {
    set(themePreferenceAtom, next);
  },
);

// 主题应用 atom — 只负责把传入的生效主题应用到 DOM
export const themeEffectAtom = atom(
  (get) => get(themeAtom),
  (_get, _set, theme: Theme) => {
    applyDarkClass(theme === "dark");
  },
);

// 初始化主题的 atom — 读当前生效主题并应用到 DOM
export const initThemeAtom = atom(null, (get, set) => {
  set(themeEffectAtom, get(themeAtom));
});
